import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { GOOGLE_LEAVE_REVIEW_URL } from '@/constants/googleReview';
import { cn } from '@/lib/utils';
import type { ComponentType, CSSProperties } from 'react';

const external = {
	target: '_blank' as const,
	rel: 'noopener noreferrer',
};

function GoogleWordmark({ className }: { className?: string }) {
	return (
		<span
			className={`font-albert inline-block text-[15px] font-bold leading-none tracking-tight ${
				className ?? ''
			}`}
			aria-label="Google"
		>
			<span className="text-[#4285F4]">G</span>
			<span className="text-[#EA4335]">o</span>
			<span className="text-[#FBBC05]">o</span>
			<span className="text-[#4285F4]">g</span>
			<span className="text-[#34A853]">l</span>
			<span className="text-[#EA4335]">e</span>
		</span>
	);
}

/**
 * Igual ao Reclame Aqui horizontal: ícone à esquerda,
 * “Deixar avaliação no” em cima e logo Google embaixo.
 */
export function GoogleReviewLongLikeRa() {
	return (
		<a
			href={GOOGLE_LEAVE_REVIEW_URL}
			{...external}
			style={{ width: 136, height: 48 }}
			className="font-albert box-border inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#1e3a5f] bg-white px-2.5"
		>
			<GoogleIcon className="size-6 shrink-0" aria-hidden />
			<span className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
				<span className="text-[9px] font-normal leading-none text-[#5f6368]">
					Deixar avaliação no
				</span>
				<GoogleWordmark />
			</span>
		</a>
	);
}

export type GoogleReviewSealSize = { width: number; height: number };

/** Fallback ~horizontal_1 do Reclame Aqui, antes do embed medir o tamanho real. */
const GOOGLE_REVIEW_SEAL_FALLBACK: GoogleReviewSealSize = {
	width: 136,
	height: 48,
};

/** Largura extra em relação ao selo RA (só width). */
const GOOGLE_REVIEW_EXTRA_WIDTH_PX = 12;

/** Botão longo — borda cinza; altura igual ao RA, largura um pouco maior. */
export function GoogleReviewLongButtonCurrent({
	sealSize,
	variant = 'default',
}: {
	sealSize?: GoogleReviewSealSize | null;
	variant?: 'default' | 'newLanding';
}) {
	const { width, height } = sealSize ?? GOOGLE_REVIEW_SEAL_FALLBACK;
	const compact = height <= 48;

	return (
		<a
			href={GOOGLE_LEAVE_REVIEW_URL}
			{...external}
			style={{ width: width + GOOGLE_REVIEW_EXTRA_WIDTH_PX, height }}
			className={cn(
				'font-albert box-border inline-flex shrink-0 items-center gap-2 rounded-lg border px-2.5 font-semibold leading-[1.15] text-[#3c4043] transition-colors',
				compact ? 'text-[10px]' : 'text-[11px]',
				variant === 'newLanding'
					? 'border-[#C8DAE8] bg-white'
					: 'border-[#9aa0a6] bg-white',
			)}
		>
			<GoogleIcon
				className={`shrink-0 ${compact ? 'size-6' : 'size-7'}`}
				aria-hidden
			/>
			<span className="min-w-0 flex-1 text-left">
				Deixar avaliação no Google
			</span>
		</a>
	);
}

type GoogleReviewLongButtonRaProps = {
	widthPx: number;
	heightPx: number;
	iconSizeClass?: string;
	textClassName?: string;
	gapClass?: string;
	paddingClass?: string;
};

/** Horizontal no estilo Reclame Aqui `horizontal_1` (ícone à esquerda, texto à direita). */
function GoogleReviewLongButtonRa({
	widthPx,
	heightPx,
	iconSizeClass = 'size-6',
	textClassName = 'text-[10px] font-semibold leading-[1.15] text-[#3c4043]',
	gapClass = 'gap-2',
	paddingClass = 'px-2.5',
}: GoogleReviewLongButtonRaProps) {
	const boxStyle: CSSProperties = { width: widthPx, height: heightPx };

	return (
		<a
			href={GOOGLE_LEAVE_REVIEW_URL}
			{...external}
			style={boxStyle}
			className={`font-albert box-border inline-flex shrink-0 items-center rounded-lg border border-[#1e3a5f] bg-white ${gapClass} ${paddingClass}`}
		>
			<GoogleIcon className={`${iconSizeClass} shrink-0`} aria-hidden />
			<span className={`min-w-0 flex-1 text-left ${textClassName}`}>
				Deixar avaliação no Google
			</span>
		</a>
	);
}

export function GoogleReviewLongRa136x48() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={136}
			heightPx={48}
			iconSizeClass="size-6"
			textClassName="text-[10px] font-semibold leading-[1.15] text-[#3c4043]"
		/>
	);
}

export function GoogleReviewLongRa152x48() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={152}
			heightPx={48}
			textClassName="text-[10px] font-semibold leading-[1.15] text-[#3c4043]"
		/>
	);
}

export function GoogleReviewLongRa168x48() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={168}
			heightPx={48}
			textClassName="text-[11px] font-semibold leading-[1.15] text-[#3c4043]"
		/>
	);
}

export function GoogleReviewLongRa184x48() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={184}
			heightPx={48}
			paddingClass="px-3"
			textClassName="text-[11px] font-semibold leading-[1.2] text-[#3c4043]"
		/>
	);
}

export function GoogleReviewLongRa136x44() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={136}
			heightPx={44}
			iconSizeClass="size-5"
			textClassName="text-[9px] font-semibold leading-[1.1] text-[#3c4043]"
		/>
	);
}

export function GoogleReviewLongRa136x56() {
	return (
		<GoogleReviewLongButtonRa
			widthPx={136}
			heightPx={56}
			iconSizeClass="size-7"
			textClassName="text-[11px] font-semibold leading-[1.2] text-[#3c4043]"
			paddingClass="px-3"
		/>
	);
}

/** Comparação de proporções do botão longo (referência: RA horizontal_1 ≈ 136×48). */
export const GOOGLE_REVIEW_LONG_PROPORTION_VARIANTS: Array<{
	id: string;
	label: string;
	Component: ComponentType<{
		sealSize?: GoogleReviewSealSize | null;
		variant?: 'default' | 'newLanding';
	}>;
}> = [
	{
		id: 'like-ra',
		label: 'Igual RA',
		Component: GoogleReviewLongLikeRa,
	},
	{
		id: 'atual',
		label: 'Atual (como está)',
		Component: GoogleReviewLongButtonCurrent,
	},
	{
		id: 'ra-136x48',
		label: 'RA 136×48',
		Component: GoogleReviewLongRa136x48,
	},
	{
		id: 'ra-152x48',
		label: '152×48',
		Component: GoogleReviewLongRa152x48,
	},
	{
		id: 'ra-168x48',
		label: '168×48',
		Component: GoogleReviewLongRa168x48,
	},
	{
		id: 'ra-184x48',
		label: '184×48',
		Component: GoogleReviewLongRa184x48,
	},
	{
		id: 'ra-136x44',
		label: '136×44',
		Component: GoogleReviewLongRa136x44,
	},
	{
		id: 'ra-136x56',
		label: '136×56',
		Component: GoogleReviewLongRa136x56,
	},
];
